/**
 * Le bouton qui lance, et qui dit où en est ce qu'il a lancé.
 *
 * Les quatre ateliers (image, vidéo, audio, outils) répétaient le même bloc :
 * `store.setState({ generating: true })` — un booléen qu'aucun abonné
 * n'écoutait —, un `btn.disabled` posé à la main, et un libellé figé à
 * « Génération… ». Le pourcentage existait pourtant et arrivait toutes les
 * une à deux secondes ; il n'allait nulle part. La barre de remplissage
 * existait elle aussi (`--gen-progress`, dans `.btn-generate::before`), mais
 * un seul atelier la nourrissait, depuis `/api/status` — donc jamais sur le
 * site, où la VM qui sert la page ne génère rien.
 *
 * Ici tout dérive de `store.run`, la seule tranche qui sache ce qui tourne.
 * Le bouton est ce que le doigt vient de toucher : c'est là que l'attente doit
 * se lire, et pas seulement neuf cents pixels plus bas.
 */
import { type RunProgress, store } from "../state/store.js";
import { h, setStyle } from "./dom.js";

export interface GenerateButtonHandle {
	element: HTMLButtonElement;
	/** Change ce que dit le bouton au repos (l'atelier Outils en a un par outil). */
	setLabel(label: string): void;
}

/**
 * @param label Ce que dit le bouton au repos (« Générer », « Lancer »…).
 * @param onClick Appelé au clic ; le bouton se désactive tout seul pendant.
 */
export function generateButton(label: string, onClick: () => void): GenerateButtonHandle {
	let restingLabel = label;
	const element = h("button", {
		class: "btn-generate",
		type: "button",
		onclick: onClick,
	}) as HTMLButtonElement;

	function render(): void {
		const run = store.get().run;
		// Une seule génération à la fois : pendant qu'une tourne, aucun bouton
		// de génération de la page ne doit en lancer une seconde.
		element.disabled = run !== null;
		element.replaceChildren(h("span", null, textFor(run)));
		setStyle(element, "--gen-progress", `${clampPercent(run?.percent)}%`);
	}

	function textFor(run: RunProgress | null): string {
		if (run === null) return restingLabel;
		// Une machine qui ne répond plus ne progresse pas : afficher son
		// dernier pourcentage contredirait le panneau qui vient de le dire.
		if (run.machineGone) return "Machine déconnectée";
		if (run.percent === null) return run.message;
		return `${run.message} ${Math.round(run.percent)} %`;
	}

	store.subscribe((s) => s.run, render);
	render();

	return {
		element,
		setLabel(next: string): void {
			restingLabel = next;
			render();
		},
	};
}

function clampPercent(percent: number | null | undefined): number {
	if (percent === null || percent === undefined || !Number.isFinite(percent)) return 0;
	return Math.max(0, Math.min(100, percent));
}
